import { useState } from 'react';
import { logError } from '@cortex/faro';
import { Card } from '@cortex/web-design-system/components/ui/Card';
import type { ValidationLogEntry } from '../../types/revenue.js';
import { RevenueMetricCard } from './RevenueMetricCard.js';

/** @graph-connects none */
interface ValidationRuleResult {
  rule: string;
  expression: string;
  status: 'PASS' | 'WARN' | 'FAIL';
  detail: string;
  group?: string;
}

/** @graph-connects none */
type GroupKey = '前受金' | '売上カテゴリ' | 'データ品質' | 'その他';

/** @graph-connects none */
const GROUP_ORDER: GroupKey[] = ['前受金', '売上カテゴリ', 'データ品質', 'その他'];

/** @graph-connects none */
function parseResults(resultsJson: string): ValidationRuleResult[] {
  try {
    const parsed: unknown = JSON.parse(resultsJson);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (item): item is ValidationRuleResult =>
        typeof item === 'object' &&
        item !== null &&
        'rule' in item &&
        'status' in item &&
        'expression' in item &&
        'detail' in item,
    );
  } catch (error: unknown) {
    logError(error instanceof Error ? error : new Error(String(error)), {
      event: 'revenue_validation_results_parse_failed',
    });
    return [];
  }
}

/** @graph-connects none */
function groupResults(results: ValidationRuleResult[]): Map<GroupKey, ValidationRuleResult[]> {
  const groups = new Map<GroupKey, ValidationRuleResult[]>();
  for (const key of GROUP_ORDER) {
    groups.set(key, []);
  }
  for (const r of results) {
    const key = (GROUP_ORDER.includes(r.group as GroupKey) ? r.group : 'その他') as GroupKey;
    groups.get(key)?.push(r);
  }
  return groups;
}

/** @graph-connects none */
function StatusBadge({ status }: { status: 'PASS' | 'WARN' | 'FAIL' }) {
  const cls =
    status === 'PASS'
      ? 'bg-[var(--color-success,#22c55e)]/10 text-[var(--color-success,#22c55e)]'
      : status === 'WARN'
        ? 'bg-yellow-500/10 text-yellow-600 dark:text-yellow-400'
        : 'bg-[var(--color-error)]/10 text-[var(--color-error)]';
  return (
    <span className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-medium ${cls}`}>
      {status}
    </span>
  );
}

/** @graph-connects none */
function GroupSummaryBar({ results }: { results: ValidationRuleResult[] }) {
  const pass = results.filter((r) => r.status === 'PASS').length;
  const warn = results.filter((r) => r.status === 'WARN').length;
  const fail = results.filter((r) => r.status === 'FAIL').length;
  if (results.length === 0) return null;

  return (
    <div className="flex items-center gap-2 text-[10px]">
      {pass > 0 && <span className="text-[var(--color-success,#22c55e)]">{String(pass)} PASS</span>}
      {warn > 0 && (
        <span className="text-yellow-600 dark:text-yellow-400">{String(warn)} WARN</span>
      )}
      {fail > 0 && (
        <span className="font-semibold text-[var(--color-error)]">{String(fail)} FAIL</span>
      )}
    </div>
  );
}

/** @graph-connects none */
function ValidationGroup({
  groupKey,
  results,
  defaultOpen,
}: {
  groupKey: GroupKey;
  results: ValidationRuleResult[];
  defaultOpen: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  if (results.length === 0) return null;

  const hasFail = results.some((r) => r.status === 'FAIL');
  const hasWarn = results.some((r) => r.status === 'WARN');

  return (
    <Card className="overflow-hidden">
      <button
        type="button"
        onClick={() => {
          setOpen(!open);
        }}
        className="flex w-full items-center justify-between px-4 py-3 text-left transition-colors hover:bg-[var(--color-bg-subtle)]"
        aria-expanded={open}
      >
        <div className="flex items-center gap-2">
          <span className="text-xs text-[var(--color-text-muted)]">{open ? '▼' : '▶'}</span>
          <span className="text-sm font-semibold text-[var(--color-text-primary)]">{groupKey}</span>
          {!open && (hasFail || hasWarn) && (
            <span
              className={`rounded-full px-1.5 py-0.5 text-[10px] font-medium ${
                hasFail
                  ? 'bg-[var(--color-error)]/10 text-[var(--color-error)]'
                  : 'bg-yellow-500/10 text-yellow-600 dark:text-yellow-400'
              }`}
            >
              {hasFail ? 'FAIL' : 'WARN'}
            </span>
          )}
        </div>
        <GroupSummaryBar results={results} />
      </button>
      {open && (
        <div className="overflow-x-auto border-t border-[var(--color-border)]">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[var(--color-bg-base)]">
                <th className="px-3 py-1.5 text-left text-xs font-medium text-[var(--color-text-muted)]">
                  ルール
                </th>
                <th className="px-3 py-1.5 text-left text-xs font-medium text-[var(--color-text-muted)]">
                  検証式
                </th>
                <th className="px-3 py-1.5 text-center text-xs font-medium text-[var(--color-text-muted)]">
                  結果
                </th>
                <th className="px-3 py-1.5 text-left text-xs font-medium text-[var(--color-text-muted)]">
                  詳細
                </th>
              </tr>
            </thead>
            <tbody>
              {results.map((result) => (
                <tr key={result.rule} className="border-t border-[var(--color-border)]">
                  <td className="whitespace-nowrap px-3 py-1.5 text-[var(--color-text-primary)]">
                    {result.rule}
                  </td>
                  <td className="px-3 py-1.5 font-mono text-xs text-[var(--color-text-secondary)]">
                    {result.expression}
                  </td>
                  <td className="px-3 py-1.5 text-center">
                    <StatusBadge status={result.status} />
                  </td>
                  <td className="px-3 py-1.5 text-xs text-[var(--color-text-secondary)]">
                    {result.detail}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

/**
 * @graph-node {Module}
 * @graph-stack ac-mens-hub
 * @graph-domain Finance
 * @graph-business 売上集計（旧Hub v0）の要対応タブ。検算ルールを「前受金」「売上カテゴリ」「データ品質」のグループに分けて折りたたみ表示し、合格/警告/不合格サマリと検算履歴を表示する。
 * @graph-connects none
 */
export function RevenueValidationView({ entries }: { entries: ValidationLogEntry[] }) {
  const latest = entries[0];
  if (!latest) {
    return (
      <Card className="mt-6 p-8 text-center">
        <p className="text-sm text-[var(--color-text-muted)]">検算履歴がまだありません。</p>
      </Card>
    );
  }

  const latestResults = parseResults(latest.resultsJson);
  const grouped = groupResults(latestResults);

  return (
    <div className="mt-6 space-y-6">
      <div className="grid grid-cols-3 gap-4">
        <RevenueMetricCard label="合格" value={String(latest.passCount)} sub="PASS" />
        <RevenueMetricCard label="警告" value={String(latest.warnCount)} sub="WARN" />
        <RevenueMetricCard label="不合格" value={String(latest.failCount)} sub="FAIL" />
      </div>

      <div className="space-y-3">
        <h3 className="text-sm font-semibold text-[var(--color-text-primary)]">
          最新検算結果（{new Date(latest.executedAt).toLocaleString('ja-JP')}）
        </h3>
        {GROUP_ORDER.map((key) => {
          const results = grouped.get(key) ?? [];
          const hasFail = results.some((r) => r.status === 'FAIL');
          const hasWarn = results.some((r) => r.status === 'WARN');
          return (
            <ValidationGroup
              key={key}
              groupKey={key}
              results={results}
              defaultOpen={hasFail || hasWarn || key === '前受金'}
            />
          );
        })}
      </div>

      <Card className="overflow-x-auto p-0">
        <h3 className="border-b border-[var(--color-border)] px-4 py-3 text-sm font-semibold text-[var(--color-text-primary)]">
          検算履歴
        </h3>
        <table className="min-w-full border-collapse text-right text-sm">
          <thead>
            <tr className="bg-[var(--color-bg-base)]">
              <th className="border-b border-[var(--color-border)] px-3 py-2 text-left font-semibold text-[var(--color-text-secondary)]">
                実行時刻
              </th>
              <th className="border-b border-[var(--color-border)] px-3 py-2 text-xs font-medium text-[var(--color-text-secondary)]">
                合格
              </th>
              <th className="border-b border-[var(--color-border)] px-3 py-2 text-xs font-medium text-[var(--color-text-secondary)]">
                警告
              </th>
              <th className="border-b border-[var(--color-border)] px-3 py-2 text-xs font-medium text-[var(--color-text-secondary)]">
                不合格
              </th>
            </tr>
          </thead>
          <tbody>
            {entries.map((entry) => (
              <tr key={entry.executedAt} className="hover:bg-[var(--color-bg-base)]">
                <td className="border-b border-[var(--color-border)] px-3 py-1.5 text-left font-mono text-xs text-[var(--color-text-primary)]">
                  {new Date(entry.executedAt).toLocaleString('ja-JP')}
                </td>
                <td className="border-b border-[var(--color-border)] px-3 py-1.5 tabular-nums text-[var(--color-text-primary)]">
                  {entry.passCount}
                </td>
                <td className="border-b border-[var(--color-border)] px-3 py-1.5 tabular-nums text-[var(--color-text-primary)]">
                  {entry.warnCount}
                </td>
                <td
                  className={`border-b border-[var(--color-border)] px-3 py-1.5 tabular-nums ${
                    entry.failCount > 0
                      ? 'font-semibold text-[var(--color-error)]'
                      : 'text-[var(--color-text-primary)]'
                  }`}
                >
                  {entry.failCount}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
